import type React from 'react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import 'katex/dist/katex.min.css';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import {
  NEGATIVE_FEEDBACK_CATEGORIES,
  type NegativeFeedbackCategory,
  submitFeedback,
} from '../api/feedback';
import type { ActiveSearch, ChatMessage, ChatStatus } from '../hooks/useChat';
import type { Citation } from '../types/api';
import Table from '../ui/Table';
import { chipLabel } from '../utils/citation';
import type { ProgressStep } from '../utils/progress';
import { isKbUrl, KB_ARTICLE_URL } from '../utils/support';
import ExternalArrowIcon from './ExternalArrowIcon';
import styles from './Message.module.css';
import ProgressLog from './ProgressLog';
import type { StatusVariant } from './StatusIndicator';
import StatusIndicator, { RippleIcon, TypewriterText } from './StatusIndicator';

// throwOnError: false so malformed LaTeX (e.g. an unclosed brace mid-stream,
// before the closing delimiter has arrived) renders as inline error text
// instead of crashing the markdown render.
const KATEX_OPTIONS = { throwOnError: false, strict: false } as const;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Citation links are matched against the backend-provided citation source_urls
// (plus the stable KB host), so the documentation host is never hardcoded and
// the frontend works with whatever docs_base_url the backend is configured with.
function citationLinkSource(citations: Citation[]): string {
  const urls = [...citations.map((c) => c.source_url), KB_ARTICLE_URL].filter(Boolean);
  return `\\[[^\\]]*\\]\\((?:${urls.map(escapeRegExp).join('|')})[^)]*\\)`;
}
function citationLinkRe(citations: Citation[]): RegExp {
  return new RegExp(` (${citationLinkSource(citations)})`, 'g');
}
function bindCitationSpaces(md: string, citations: Citation[]): string {
  return md.replace(citationLinkRe(citations), ' $1');
}

// The elaborator sometimes cites the same page twice in a row, occasionally with
// different section fragments. A chip's label and number both come from the matched
// Citation, which is keyed on the URL without its fragment, so those arrive as two
// visually identical chips side by side and read as a rendering bug. Collapse each
// run of adjacent citations down to the first chip per target page.
function dedupeAdjacentCitations(md: string, citations: Citation[]): string {
  const one = citationLinkSource(citations);
  return md.replace(new RegExp(`${one}(?:\\s*${one})+`, 'g'), (run) => {
    const seen = new Set<string>();
    const kept: string[] = [];
    for (const link of run.match(new RegExp(one, 'g')) ?? []) {
      const href = link.slice(link.lastIndexOf('](') + 2, -1);
      const page = href.split('#')[0].replace(/\/$/, '');
      if (seen.has(page)) continue;
      seen.add(page);
      kept.push(link);
    }
    return kept.join(' ');
  });
}

// The elaborator isn't instructed on math delimiter syntax, so it sometimes
// emits LaTeX-native \(...\) / \[...\] instead of the $...$ / $$...$$ that
// remark-math parses. Normalize both to dollar delimiters before rendering.
function normalizeLatexDelimiters(md: string): string {
  return md
    .replace(/\\\[([\s\S]+?)\\\]/g, (_m, expr: string) => `\n$$\n${expr.trim()}\n$$\n`)
    .replace(/\\\(([^\n]+?)\\\)/g, (_m, expr: string) => `$${expr.trim()}$`);
}

function matchCitation(href: string, citations: Citation[]): Citation | null {
  const baseUrl = href.split('#')[0];
  return (
    citations.find((c) => c.source_url === baseUrl) ??
    citations.find((c) => c.source_url === `${baseUrl}/`) ??
    null
  );
}

function makeAnchor(
  onDocLinkClick?: (url: string, citations: Citation[]) => void,
  citations?: Citation[],
  activeDocUrl?: string | null,
  historyCitations?: Citation[],
) {
  return function Anchor({ href, children }: { href?: string; children?: React.ReactNode }) {
    const isKbLink = isKbUrl(href);
    const matched = href ? matchCitation(href, citations ?? []) : null;
    if (href && (matched || isKbLink)) {
      const number = matched ? (citations ?? []).indexOf(matched) + 1 : null;
      const isKbCitation = matched?.source === 'kb' || isKbLink;
      // Mirror the context panel: show only the page/article title, not the
      // full section heading the elaborator linked (e.g. "Simulation Setup").
      const labelText = matched ? chipLabel(matched) : null;
      const label = labelText ?? children;
      if (number !== null && isKbCitation) {
        return (
          <a
            href={href}
            className={styles.citationChip}
            target="_blank"
            rel="noopener noreferrer"
            title={labelText ?? undefined}
            aria-label={`Open source ${number}${labelText ? `: ${labelText}` : ''} in a new tab`}
          >
            <span className={styles.citationChipLabel}>{label}</span>
            <sup className={styles.citationChipNum}>{number}</sup>
            <ExternalArrowIcon className={styles.citationExternal} />
          </a>
        );
      }
      if (number !== null && onDocLinkClick) {
        // Highlight the chip whose doc is currently open in the context panel.
        const isActive = !!activeDocUrl && matchCitation(activeDocUrl, citations ?? []) === matched;
        return (
          <a
            href={href}
            className={`${styles.citationChip}${isActive ? ` ${styles.citationChipActive}` : ''}`}
            onClick={(e) => {
              e.preventDefault();
              onDocLinkClick(href, citations ?? []);
            }}
            title={labelText ?? undefined}
            aria-label={`Open source ${number}${labelText ? `: ${labelText}` : ''}`}
          >
            <span className={styles.citationChipLabel}>{label}</span>
            <sup className={styles.citationChipNum}>{number}</sup>
          </a>
        );
      }
    }
    // History-only answers reference pages cited earlier in the conversation but
    // carry no citations of their own, so they arrive as plain links. Route them
    // to the side panel (not a new tab) when the href matches a citation seen
    // anywhere in this conversation. No docs host is hardcoded (see issue #78).
    if (href && !isKbLink && onDocLinkClick && matchCitation(href, historyCitations ?? [])) {
      return (
        <a
          href={href}
          onClick={(e) => {
            e.preventDefault();
            onDocLinkClick(href, citations ?? []);
          }}
        >
          {children}
        </a>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  };
}

// The design system's Table, which also gives a table wider than the 68ch column
// its own sideways scroll, so the message never scrolls the page.
function MarkdownTable({
  node: _node,
  children,
  ...props
}: React.ComponentProps<'table'> & { node?: unknown }) {
  return (
    <div className={styles.tableBlock}>
      <Table {...props}>{children}</Table>
    </div>
  );
}

const CHARS_PER_FRAME = 6; // ~360 chars/sec at 60fps

// Switch this to preview each animation:
// 'ripple' | 'wave' | 'tide' | 'drop' | 'sonar' | 'dhiwave' | 'capillary'
const STATUS_VARIANT: StatusVariant = 'capillary';

type FeedbackState = 'idle' | 'submitting' | 'positive' | 'negative';
type Polarity = 'positive' | 'negative';

function FeedbackButtons({
  traceId,
  sessionId,
  onActiveChange,
}: {
  traceId: string;
  sessionId: string;
  onActiveChange?: (active: boolean) => void;
}) {
  const [feedbackState, setFeedbackState] = useState<FeedbackState>('idle');
  const [polarity, setPolarity] = useState<Polarity | null>(null);
  const [comment, setComment] = useState('');
  const [categories, setCategories] = useState<NegativeFeedbackCategory[]>([]);
  const [showThankYou, setShowThankYou] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showModal = polarity !== null;

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  // Pin the action row visible while modal is open or thank-you is showing
  useEffect(() => {
    onActiveChange?.(showModal || showThankYou);
  }, [showModal, showThankYou, onActiveChange]);

  const onSubmitted = (result: Polarity) => {
    setFeedbackState(result);
    setShowThankYou(true);
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setFeedbackState('idle');
      setShowThankYou(false);
    }, 2500);
  };

  const openPanel = (next: Polarity) => {
    if (feedbackState === 'submitting') return;
    setComment('');
    setCategories([]);
    setPolarity(next);
  };

  const handleCancel = () => setPolarity(null);

  const toggleCategory = (value: NegativeFeedbackCategory) => {
    setCategories((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value],
    );
  };

  const handleSubmit = async () => {
    if (polarity === null) return;
    const score: 1 | -1 = polarity === 'positive' ? 1 : -1;
    const trimmed = comment.trim() || undefined;
    const cats = polarity === 'negative' ? categories : [];
    setPolarity(null);
    setFeedbackState('submitting');
    try {
      await submitFeedback(traceId, sessionId, score, trimmed, cats);
      onSubmitted(polarity);
    } catch {
      setFeedbackState('idle');
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  };

  const isDisabled = feedbackState === 'submitting';
  const isPositive = polarity === 'positive';
  const title = isPositive ? 'Give positive feedback' : 'Give negative feedback';
  const commentLabel = isPositive
    ? 'What worked well with this response?'
    : 'What went wrong with this response?';
  const commentPlaceholder = isPositive
    ? 'Tell us what worked well...'
    : 'Tell us how it can be improved...';

  return (
    <div className={styles.feedbackButtons}>
      <span className={styles.feedbackPrompt}>Was this helpful?</span>
      <button
        type="button"
        className={`${styles.feedbackBtn}${feedbackState === 'positive' ? ` ${styles.feedbackBtnPositive}` : ''}`}
        onClick={() => openPanel('positive')}
        disabled={isDisabled}
        title="Good response"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 7V13H11.5L13 10V7H10L9.5 3C9.5 2.4 9 2 8.5 2C8 2 7.5 2.4 7.5 3V7H5Z" />
          <path d="M5 7H3V13H5" />
        </svg>
      </button>
      <button
        type="button"
        className={`${styles.feedbackBtn}${feedbackState === 'negative' ? ` ${styles.feedbackBtnNegative}` : ''}`}
        onClick={() => openPanel('negative')}
        disabled={isDisabled}
        title="Bad response"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M11 9V3H4.5L3 6V9H6L6.5 13C6.5 13.6 7 14 7.5 14C8 14 8.5 13.6 8.5 13V9H11Z" />
          <path d="M11 9H13V3H11" />
        </svg>
      </button>
      {showThankYou && <span className={styles.feedbackThankYou}>Thanks for the feedback!</span>}
      {showModal &&
        createPortal(
          // biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss is a mouse convenience; Cancel button is the keyboard path
          // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop dismiss is a mouse convenience; Cancel button is the keyboard path
          <div
            className={styles.feedbackOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) handleCancel();
            }}
          >
            <div className={styles.feedbackModalCard}>
              <p className={styles.feedbackModalTitle}>{title}</p>
              {!isPositive && (
                <div className={styles.feedbackFieldLabel}>
                  <span className={styles.feedbackFieldLabelText}>
                    Categories <span className={styles.feedbackModalOptional}>(optional)</span>
                  </span>
                  <div className={styles.feedbackCheckboxGroup}>
                    {NEGATIVE_FEEDBACK_CATEGORIES.map((opt) => (
                      <label key={opt.value} className={styles.feedbackCheckboxOption}>
                        <input
                          type="checkbox"
                          checked={categories.includes(opt.value)}
                          onChange={() => toggleCategory(opt.value)}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <label className={styles.feedbackFieldLabel}>
                <span className={styles.feedbackFieldLabelText}>
                  {commentLabel} <span className={styles.feedbackModalOptional}>(optional)</span>
                </span>
                <textarea
                  ref={textareaRef}
                  className={styles.feedbackTextarea}
                  value={comment}
                  onChange={handleCommentChange}
                  placeholder={commentPlaceholder}
                  // biome-ignore lint/a11y/noAutofocus: feedback modal opened by explicit user action; expected to focus the textarea
                  autoFocus
                />
              </label>
              <div className={styles.feedbackPopupActions}>
                <button type="button" className={styles.feedbackCancelBtn} onClick={handleCancel}>
                  Cancel
                </button>
                <button type="button" className={styles.feedbackSubmitBtn} onClick={handleSubmit}>
                  Submit
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function BotAvatar() {
  return (
    <div className={styles.avatar}>
      <img
        src={`${import.meta.env.BASE_URL}favicon.svg`}
        alt="DHI Docs AI"
        className={styles.avatarIcon}
      />
    </div>
  );
}

interface FinishedMessageProps {
  message: ChatMessage;
  sessionId: string;
  onOpenContext?: (citations: Citation[]) => void;
  onDocLinkClick?: (url: string, citations: Citation[]) => void;
  activeDocUrl?: string | null;
  historyCitations?: Citation[];
  isLast?: boolean;
  /** Shown under the answer and above its feedback row: a setup question's options (#489). */
  after?: ReactNode;
}

/** The number on the sources pill: the sources the answer cites, when that is known (#474). */
export function sourcesCount(citations: Citation[]): number {
  const known = citations.length > 0 && citations.every((c) => typeof c.cited === 'boolean');
  return known ? citations.filter((c) => c.cited).length || citations.length : citations.length;
}

export function FinishedMessage({
  message,
  sessionId,
  onOpenContext,
  onDocLinkClick,
  activeDocUrl,
  historyCitations,
  isLast,
  after,
}: FinishedMessageProps) {
  const [feedbackActive, setFeedbackActive] = useState(false);

  if (message.role === 'user') {
    return (
      <div className={`${styles.message} ${styles.userMsg}`}>
        <div className={styles.userBubbleWrapper}>
          {message.imageRefs && message.imageRefs.length > 0 && (
            <div className={styles.userAttachments}>
              {message.imageRefs.map((ref) => (
                <span key={ref.blob_path} className={styles.userAttachmentChip}>
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                  <span className={styles.userAttachmentName}>{ref.original_filename}</span>
                </span>
              ))}
            </div>
          )}
          <div className={styles.bubble}>{message.content}</div>
        </div>
      </div>
    );
  }

  if (message.status === 'stopped') {
    return (
      <div className={`${styles.message} ${styles.botMsg}`}>
        <div className={styles.messageHeader}>
          <BotAvatar />
          <div className={styles.nameRow}>
            <span className={styles.name}>DHI Docs AI</span>
          </div>
        </div>
        <div className={`${styles.body} ${styles.stoppedNotice}`}>
          DHI Docs AI's response was stopped by the user.
        </div>
      </div>
    );
  }

  const citations = message.citations ?? [];
  const hasContext = citations.length > 0;

  return (
    <div className={`${styles.message} ${styles.botMsg}`}>
      <div className={styles.messageHeader}>
        <BotAvatar />
        <div className={styles.nameRow}>
          <span className={styles.name}>DHI Docs AI</span>
          {hasContext && onOpenContext && (
            <button
              type="button"
              className={styles.sourcesPill}
              onClick={() => onOpenContext(citations)}
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M3 2h7l3 3v9a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" />
                <polyline points="10 2 10 5 13 5" />
              </svg>
              {sourcesCount(citations)} {sourcesCount(citations) === 1 ? 'source' : 'sources'}
            </button>
          )}
        </div>
      </div>
      <div className={styles.body}>
        {message.progress && message.progress.length > 0 && (
          <ProgressLog
            steps={message.progress}
            live={false}
            onOpenLink={onDocLinkClick ? (url) => onDocLinkClick(url, citations) : undefined}
          />
        )}
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[[rehypeKatex, KATEX_OPTIONS]]}
          components={{
            a: makeAnchor(onDocLinkClick, citations, activeDocUrl, historyCitations),
            table: MarkdownTable,
          }}
        >
          {bindCitationSpaces(
            dedupeAdjacentCitations(normalizeLatexDelimiters(message.content), citations),
            citations,
          )}
        </ReactMarkdown>
      </div>
      {after}
      {message.traceId && (
        <div
          className={`${styles.botActionRow}${isLast || feedbackActive ? ` ${styles.botActionRowAlways}` : ''}`}
        >
          <FeedbackButtons
            traceId={message.traceId}
            sessionId={sessionId}
            onActiveChange={setFeedbackActive}
          />
        </div>
      )}
    </div>
  );
}

const SEARCH_STEP_STATUS_CLASS: Record<ActiveSearch['status'], string> = {
  pending: styles.searchStepPending,
  done: styles.searchStepDone,
  failed: styles.searchStepFailed,
};

// Two or more documentation_retrieval goals in the same planner iteration:
// shown as independently-resolving rows instead of the single StatusIndicator,
// so concurrent searches read as concurrent rather than one flickering line.
// The icon slot is a fixed size across all three states so a row's height
// doesn't jump when it resolves.
function SearchStepList({ steps }: { steps: ActiveSearch[] }) {
  return (
    <div className={styles.searchSteps}>
      {steps.map((s) => (
        <div key={s.id} className={`${styles.searchStep} ${SEARCH_STEP_STATUS_CLASS[s.status]}`}>
          <span className={styles.searchStepIcon}>
            {s.status === 'pending' && <RippleIcon size={24} />}
            {s.status === 'done' && (
              <svg
                aria-hidden="true"
                viewBox="0 0 16 16"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline
                  points="3.5 8.5 6.5 11.5 12.5 4.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {s.status === 'failed' && (
              <svg
                aria-hidden="true"
                viewBox="0 0 16 16"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="4" y1="4" x2="12" y2="12" strokeLinecap="round" />
                <line x1="12" y1="4" x2="4" y2="12" strokeLinecap="round" />
              </svg>
            )}
          </span>
          <TypewriterText key={s.message} text={s.message} className={styles.searchStepLabel} />
        </div>
      ))}
    </div>
  );
}

interface StreamingMessageProps {
  content: string;
  status: ChatStatus;
  statusMessage: string;
  activeSearches: ActiveSearch[];
  progress?: ProgressStep[];
  streamingCitations?: Citation[];
  onOpenContext?: (citations: Citation[]) => void;
  onDocLinkClick?: (url: string, citations: Citation[]) => void;
  activeDocUrl?: string | null;
  historyCitations?: Citation[];
  onDrainComplete?: () => void;
}

export function StreamingMessage({
  content,
  status,
  statusMessage,
  activeSearches,
  progress = [],
  streamingCitations,
  onOpenContext,
  onDocLinkClick,
  activeDocUrl,
  historyCitations,
  onDrainComplete,
}: StreamingMessageProps) {
  const citationList = streamingCitations ?? [];
  const hasCitations = citationList.length > 0;

  const [displayContent, setDisplayContent] = useState('');
  const [isDraining, setIsDraining] = useState(false);
  const pendingRef = useRef('');
  const rafRef = useRef<number | null>(null);
  const prevLenRef = useRef(0);
  const drainRef = useRef<() => void>(() => {});
  const streamEndedRef = useRef(false);
  const onDrainCompleteRef = useRef(onDrainComplete);
  onDrainCompleteRef.current = onDrainComplete;

  drainRef.current = () => {
    if (pendingRef.current.length === 0) {
      rafRef.current = null;
      setIsDraining(false);
      if (streamEndedRef.current) {
        onDrainCompleteRef.current?.();
      }
      return;
    }
    const chunk = pendingRef.current.slice(0, CHARS_PER_FRAME);
    pendingRef.current = pendingRef.current.slice(CHARS_PER_FRAME);
    setDisplayContent((prev) => prev + chunk);
    rafRef.current = requestAnimationFrame(() => drainRef.current());
  };

  // Feed new raw chars into the pending buffer
  useEffect(() => {
    const newChars = content.slice(prevLenRef.current);
    prevLenRef.current = content.length;
    if (!newChars) return;
    pendingRef.current += newChars;
    setIsDraining(true);
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => drainRef.current());
    }
  }, [content]);

  // When stream ends, let the drain loop finish naturally then signal the parent.
  // Reset when a new stream begins so a leftover drain can't misfire.
  useEffect(() => {
    if (status === 'idle' || status === 'error') {
      streamEndedRef.current = true;
      if (pendingRef.current.length === 0 && !rafRef.current) {
        onDrainCompleteRef.current?.();
      }
    } else {
      streamEndedRef.current = false;
    }
  }, [status]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const showCursor = status === 'generating' || isDraining;

  return (
    <div className={`${styles.message} ${styles.botMsg}`}>
      <div className={styles.messageHeader}>
        <BotAvatar />
        <div className={styles.nameRow}>
          <span className={styles.name}>DHI Docs AI</span>
          {hasCitations && onOpenContext && (
            <button
              type="button"
              className={styles.sourcesPill}
              onClick={() => onOpenContext(citationList)}
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M3 2h7l3 3v9a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" />
                <polyline points="10 2 10 5 13 5" />
              </svg>
              {sourcesCount(citationList)} {sourcesCount(citationList) === 1 ? 'source' : 'sources'}
            </button>
          )}
        </div>
      </div>
      <div className={`${styles.body}${showCursor ? ` ${styles.bodyStreaming}` : ''}`}>
        {displayContent ? (
          <>
            {progress.length > 0 && (
              <ProgressLog
                steps={progress}
                live={false}
                onOpenLink={onDocLinkClick ? (url) => onDocLinkClick(url, citationList) : undefined}
              />
            )}
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[[rehypeKatex, KATEX_OPTIONS]]}
              components={{
                a: makeAnchor(onDocLinkClick, citationList, activeDocUrl, historyCitations),
                table: MarkdownTable,
              }}
            >
              {bindCitationSpaces(
                dedupeAdjacentCitations(normalizeLatexDelimiters(displayContent), citationList),
                citationList,
              )}
            </ReactMarkdown>
          </>
        ) : progress.length > 0 ? (
          <ProgressLog
            steps={progress}
            live
            onOpenLink={onDocLinkClick ? (url) => onDocLinkClick(url, citationList) : undefined}
          />
        ) : activeSearches.length > 1 ? (
          <SearchStepList steps={activeSearches} />
        ) : (
          <StatusIndicator
            message={statusMessage || 'Searching documentation...'}
            variant={STATUS_VARIANT}
          />
        )}
      </div>
    </div>
  );
}

interface ErrorMessageProps {
  message: string;
}

export function ErrorMessage({ message }: ErrorMessageProps) {
  return (
    <div className={`${styles.message} ${styles.errorMsg}`}>
      <div className={styles.avatarError}>!</div>
      <div className={styles.errorBody}>{message}</div>
    </div>
  );
}

interface InProgressMessageProps {
  statusEvents?: Array<{ stage: string; message: string }>;
}

export function InProgressMessage({ statusEvents }: InProgressMessageProps) {
  const lastEvent =
    statusEvents && statusEvents.length > 0 ? statusEvents[statusEvents.length - 1] : null;
  const label = lastEvent?.message ?? 'Generating response...';

  return (
    <div className={`${styles.message} ${styles.botMsg}`}>
      <div className={styles.messageHeader}>
        <BotAvatar />
        <div className={styles.nameRow}>
          <span className={styles.name}>DHI Docs AI</span>
        </div>
      </div>
      <div className={styles.body}>
        <StatusIndicator message={label} variant={STATUS_VARIANT} />
      </div>
    </div>
  );
}
